import React, { useState, useEffect } from "react";
import { useUnit } from "effector-react";
import { Calendar } from "primereact/calendar";
import { Drawer } from "../../../core/components/drawer/Drawer";
import { SvgIcon } from "../../../core/components/svgIcon/SvgIcon";
import { Tooltip } from "../../../core/components/tooltip/Tooltip";
import {
  $sectorsTerms,
  $programmeTerms,
  $expenditureTerms,
} from "@/core/store/terms";
import { Term } from "@/core/store/terms/types";
import { fetchCallsRequested, $callsStore } from "../store";
import { CallFilters } from "../model/types";
import "./FiltersDrawer.scss";
import { imageList } from "@/core/utils/imageList";

export interface FiltersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
  appliedFilters?: {
    fromDate: Date | null;
    toDate: Date | null;
    programmes: string[];
    sectors: string[];
    expenditures: string[];
  };
  type?: string;
  matchingCalls?: boolean;
}

export interface FilterState {
  fromDate: Date | null;
  toDate: Date | null;
  programmes: string[];
  sectors: string[];
  expenditures: string[];
  programmeSearch: string;
  sectorSearch: string;
}

const initialFilterState: FilterState = {
  fromDate: null,
  toDate: null,
  programmes: [],
  sectors: [],
  expenditures: [],
  programmeSearch: "",
  sectorSearch: "",
};

// Helper functions for term data
const getTopLevelTerms = (terms: Term[]): Term[] => {
  return terms.filter((term) => term.depth === 0 || term.parent === "0");
};

const getChildTerms = (terms: Term[], parentTid: string): Term[] => {
  return terms.filter((term) => term.parent === parentTid);
};

// Helper function to convert CallFilters to FilterState
const parseFiltersFromStore = (callFilters: CallFilters): FilterState => {
  const parseDate = (dateString?: string): Date | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  const parseCommaSeparatedValues = (value?: string): string[] => {
    if (!value || value.trim() === "") return [];
    return value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v !== "");
  };

  return {
    fromDate: parseDate(callFilters.deadline_from_date),
    toDate: parseDate(callFilters.deadline_to_date),
    programmes: parseCommaSeparatedValues(callFilters.programmes),
    sectors: parseCommaSeparatedValues(callFilters.sectors),
    expenditures: parseCommaSeparatedValues(callFilters.expenditures),
    programmeSearch: "",
    sectorSearch: "",
  };
};

export const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  isOpen,
  onClose,
  onApply,
  appliedFilters,
  type,
  matchingCalls,
}) => {
  const [filters, setFilters] = useState<FilterState>(initialFilterState);
  const [expandedSectors, setExpandedSectors] = useState<string[]>([]);
  const [showMoreProgrammes, setShowMoreProgrammes] = useState(false);

  // Get terms data from store
  const sectorsTermsState = useUnit($sectorsTerms);
  const programmeTermsState = useUnit($programmeTerms);
  const expenditureTermsState = useUnit($expenditureTerms);
  const callsState = useUnit($callsStore);

  // Extract terms data
  const sectorsData = sectorsTermsState.data || [];
  const programmesData = programmeTermsState.data || [];
  const expendituresData = expenditureTermsState.data || [];

  // Get top-level sectors for main list
  const topLevelSectors = getTopLevelTerms(sectorsData);

  // Initialize filters when drawer opens or appliedFilters change
  useEffect(() => {
    if (isOpen) {
      const initialFilters = appliedFilters || initialFilterState;
      setFilters({
        ...initialFilters,
        programmeSearch: "",
        sectorSearch: "",
      });

      // Expand sectors that have selected children
      const selectedSectors = initialFilters.sectors;
      const sectorsToExpand: string[] = [];

      selectedSectors.forEach((selectedSectorTid) => {
        // Check if this is a child sector
        const parentSector = sectorsData.find((sector) =>
          getChildTerms(sectorsData, sector.tid).some(
            (child) => child.tid === selectedSectorTid,
          ),
        );
        if (parentSector && !sectorsToExpand.includes(parentSector.tid)) {
          sectorsToExpand.push(parentSector.tid);
        }
      });

      setExpandedSectors(sectorsToExpand);
    }
  }, [isOpen, appliedFilters, sectorsData]);

  const handleCheckboxChange = (category: keyof FilterState, value: string) => {
    if (Array.isArray(filters[category])) {
      const currentValues = filters[category] as string[];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      setFilters({ ...filters, [category]: newValues });
    }
  };

  const handleSectorToggle = (sectorTid: string) => {
    setExpandedSectors((prev) =>
      prev.includes(sectorTid)
        ? prev.filter((s) => s !== sectorTid)
        : [...prev, sectorTid],
    );
  };

  const filteredProgrammes = programmesData.filter((programme) =>
    programme.name
      .toLowerCase()
      .includes(filters.programmeSearch.toLowerCase()),
  );

  const filteredSectors = topLevelSectors.filter((sector) =>
    sector.name.toLowerCase().includes(filters.sectorSearch.toLowerCase()),
  );

  const handleClearAll = () => {
    // Clear local filter state
    setFilters(initialFilterState);
    setExpandedSectors([]);
    setShowMoreProgrammes(false);

    // Clear filters from store and refresh calls
    fetchCallsRequested({});

    // Call the onApply callback with empty filters to update parent component
    onApply(initialFilterState);
    onClose();
  };

  const handleApply = () => {
    // Helper function to format date as YYYY-MM-DD
    const formatDate = (date: Date | null): string | undefined => {
      if (!date) return undefined;
      return date.toISOString().split("T")[0];
    };

    // Convert FilterState to CallFilters format and merge with existing filters
    const newFilters: CallFilters = {
      // Preserve existing filters from the store
      ...callsState.filters,
      // Add the new filter values
      sectors:
        filters.sectors.length > 0 ? filters.sectors.join(",") : undefined,
      programmes:
        filters.programmes.length > 0
          ? filters.programmes.join(",")
          : undefined,
      expenditures:
        filters.expenditures.length > 0
          ? filters.expenditures.join(",")
          : undefined,
      deadline_from_date: formatDate(filters.fromDate),
      deadline_to_date: formatDate(filters.toDate),
      type: type === "for-you" ? "Eligible" : "",
    };

    // Remove undefined values
    const cleanFilters = Object.fromEntries(
      Object.entries(newFilters).filter(
        ([_, value]) => value !== undefined && value !== "",
      ),
    ) as CallFilters;

    // Fetch calls with filters
    fetchCallsRequested(cleanFilters);

    // Call the original onApply callback
    onApply(filters);
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Filters" width="400px">
      <div className="filters-drawer">
        <p className="filters-description">Apply filters to calls.</p>

        {/* Date Range */}
        <div className="filter-section">
          <h3>
            Deadline range{" "}
            <Tooltip
              text="Select the date range for the deadline of calls to be shown"
              position="top"
            >
              <img src={imageList.helpIcon} />
            </Tooltip>
          </h3>
          <div className="date-range">
            <div className="date-field">
              <div className="date-input-wrapper">
                <SvgIcon icon="icon-clock" />
                <Calendar
                  value={filters.fromDate}
                  onChange={(e) =>
                    setFilters({ ...filters, fromDate: e.value as Date | null })
                  }
                  placeholder="DD/MM/YYYY"
                  dateFormat="dd/mm/yy"
                  showIcon={false}
                  className="date-picker"
                  maxDate={filters.toDate || undefined}
                />
              </div>
            </div>
            <div className="date-field">
              <div className="date-input-wrapper">
                <SvgIcon icon="icon-clock" />
                <Calendar
                  value={filters.toDate}
                  onChange={(e) =>
                    setFilters({ ...filters, toDate: e.value as Date | null })
                  }
                  placeholder="DD/MM/YYYY"
                  dateFormat="dd/mm/yy"
                  showIcon={false}
                  className="date-picker"
                  minDate={filters.fromDate || undefined}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Programmes */}
        <div className="filter-section">
          <h3>
            Programmes{" "}
            <Tooltip
              text="Choose one or more funding programmes to filter the calls list"
              position="top"
            >
              <img src={imageList.helpIcon} />
            </Tooltip>
          </h3>
          <div className="search-wrapper">
            <SvgIcon icon="icon-filter" />
            <input
              type="text"
              placeholder="Search Programmes"
              value={filters.programmeSearch}
              onChange={(e) =>
                setFilters({ ...filters, programmeSearch: e.target.value })
              }
            />
          </div>
          <div className="checkbox-list">
            {filteredProgrammes
              .slice(0, showMoreProgrammes ? filteredProgrammes.length : 3)
              .map((programme) => (
                <label key={programme.tid} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={filters.programmes.includes(programme.tid)}
                    onChange={() =>
                      handleCheckboxChange("programmes", programme.tid)
                    }
                  />
                  <span className="checkmark"></span>
                  {programme.name}
                </label>
              ))}
            {filteredProgrammes.length > 3 && (
              <button
                className="show-more-btn"
                onClick={() => setShowMoreProgrammes(!showMoreProgrammes)}
              >
                Show{" "}
                {showMoreProgrammes
                  ? "less"
                  : `${filteredProgrammes.length - 3} more`}
              </button>
            )}
          </div>
        </div>

        {/* Sectors */}
        {!matchingCalls && (
          <div className="filter-section">
            <h3>
              Sectors{" "}
              <Tooltip
                text="Filter calls by the sectors of your interest."
                position="top"
              >
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            <div className="search-wrapper">
              <SvgIcon icon="icon-filter" />
              <input
                type="text"
                placeholder="Search Sectors"
                value={filters.sectorSearch}
                onChange={(e) =>
                  setFilters({ ...filters, sectorSearch: e.target.value })
                }
              />
            </div>
            <div className="checkbox-list">
              {filteredSectors.map((sector) => {
                const childSectors = getChildTerms(sectorsData, sector.tid);
                const hasChildren = childSectors.length > 0;

                return (
                  <div key={sector.tid}>
                    <div className="checkbox-item expandable">
                      {hasChildren ? (
                        <button
                          className="expand-btn"
                          onClick={() => handleSectorToggle(sector.tid)}
                        >
                          <SvgIcon
                            icon={
                              expandedSectors.includes(sector.tid)
                                ? "icon-chevron-down"
                                : "icon-chevron-right"
                            }
                          />
                        </button>
                      ) : (
                        <span className="expand-btn-spacer" />
                      )}
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={filters.sectors.includes(sector.tid)}
                          onChange={() =>
                            handleCheckboxChange("sectors", sector.tid)
                          }
                        />
                        <span className="checkmark"></span>
                        {sector.name}
                      </label>
                    </div>
                    {hasChildren && expandedSectors.includes(sector.tid) && (
                      <div className="subsector-list">
                        {childSectors.map((subsector) => (
                          <label
                            key={subsector.tid}
                            className="checkbox-item subsector"
                          >
                            <input
                              type="checkbox"
                              checked={filters.sectors.includes(subsector.tid)}
                              onChange={() =>
                                handleCheckboxChange("sectors", subsector.tid)
                              }
                            />
                            <span className="checkmark"></span>
                            {subsector.name}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Expenditures */}
        {!matchingCalls && (
          <div className="filter-section">
            <h3>
              Expenditures{" "}
              <Tooltip
                text="Filter calls based on the types of eligible expenses covered by"
                position="top"
              >
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            <div className="checkbox-list">
              {expendituresData.map((expenditure) => (
                <label key={expenditure.tid} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={filters.expenditures.includes(expenditure.tid)}
                    onChange={() =>
                      handleCheckboxChange("expenditures", expenditure.tid)
                    }
                  />
                  <span className="checkmark"></span>
                  {expenditure.name}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="filter-actions">
          <button className="clear-btn" onClick={handleClearAll}>
            Clear all
          </button>
          <div className="action-buttons">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button className="apply-btn" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
